import { EVENT_INTERACTION } from '@eventbrite/consumer-search.event-renderer';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    EventInteraction,
    EventInteractionContext,
    GetTrackingInformation,
    InteractionCustomEvent,
    PLACE_EVENT_INTERACTION,
} from '@/src/homepage/utils/tracking';
import { startEventCardTracking } from './startEventCardTracking';
import { usePicoXTrackBridge } from './picoxTrackBridge';

type EventHandlerCallback = (event: string, callback: (e: InteractionCustomEvent) => void) => void;

export interface TrackableElement {
    addEventListener: EventHandlerCallback;
    removeEventListener: EventHandlerCallback;
    dispatchEvent: (e: CustomEvent) => void;
}

/**
 * Wires an event-card surface into the interaction tracking pipeline.
 *
 * Ported from `@eventbrite/consumer-search.discover-utils` (which the homepage
 * previously imported) and extended with `usePicoXTrackBridge` so the same
 * tracker emits both `log_engagement` and PicoX. The returned ref must be
 * attached to the container promoted cards bubble `EVENT_INTERACTION` up to.
 * Each interaction is enriched with caller context and re-dispatched as
 * `PLACE_EVENT_INTERACTION` (bubbling to `document`), where the singleton
 * `EventsInteractionsTracker` batches and dedupes it once and fans it out to
 * every registered logger:
 *
 *   - `InteractionsLoggerV1` → `/api/v3/log_engagement/` (Core engagement).
 *   - `InteractionsPicoXLogger` → PicoX (`ads_promoted_eb_event_impression` /
 *     `_click`, `product_location: 'homepage'`).
 *
 * Emitting PicoX from the tracker (rather than firing it directly here) means it
 * shares the tracker's `(id, placementId, action)` impression dedupe, so PicoX
 * spend stays 1:1 with the log_engagement impressions. Browser/device context is
 * captured automatically by the BSP PicoX provider; we emit ad-domain fields only.
 */
export function useTracking<T extends TrackableElement>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getTrackingInformation: GetTrackingInformation<any>,
) {
    useMemo(() => startEventCardTracking(), []);
    // Captures the BSP PicoX track fn so the document-level PicoX logger can emit.
    usePicoXTrackBridge();

    const logInteractions = useCallback(
        (e: InteractionCustomEvent) => {
            e.preventDefault();

            const detail: EventInteraction<EventInteractionContext> = {
                ...e.detail,
                ...getTrackingInformation(e.detail),
            };

            const placeEventInteractionEvent = new CustomEvent(PLACE_EVENT_INTERACTION, {
                detail,
                bubbles: true,
            });
            e.currentTarget?.dispatchEvent(placeEventInteractionEvent);
        },
        [getTrackingInformation],
    );

    const ref = useRef<T>(null);

    useEffect(() => {
        const container = ref.current;
        container?.addEventListener(EVENT_INTERACTION, logInteractions);
        return () => container?.removeEventListener(EVENT_INTERACTION, logInteractions);
    }, [ref, logInteractions]);

    return { ref };
}
